import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createCarbonApi from 'api/carbonOffset';
import createEmployeeBenefitApi from 'api/employeeBenefit';
import { cloneDeep, uniq } from 'lodash-es';
import { addFakeUsers } from 'modules/allUsers/allUsersSlice';
import { createSubscription, updateSubscription } from 'modules/subscriptions/subscriptionSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { currentUserCountryCodeSelector, currentUserSelector } from 'modules/user/userSlice';
import { AppThunk, RootState } from 'store';
import { getPlan, plans } from 'utils/planMapper';
import { getUserSeats } from 'modules/allUsers/allUsersSlice';
import { appContextSelector, supportedCountriesISO2Selector } from 'modules/app/appSlice';
import { setTag, tags } from 'utils/tags';
import { roundNumberToTwo } from 'utils/numberFormatter';

type ProcessType = 'create' | 'update' | 'cancel';

interface State {
  items: SubscriptionItem[];
  clonedItems: SubscriptionItem[];
  isLoading: boolean;
  error?: string;
  errorType?: string;
  subscriptionId?: string;
  latestOrderDate?: string;
  currency: string;
  process: ProcessType;
  currentStep: string;
  cancelAtPeriodEnd?: boolean;
  newEmployeeOffsets?: NewEmployeeOffset[];
  newEmployeeInvites?: UserPayload[];
  selectedPlanId: string;
}

interface EditProps {
  index: number;
  value: number;
  price?: number;
}

interface CreateProps {
  price?: number;
  kilosCo2?: number;
  plan?: string;
  employees?: number;
  countryName?: string;
  countryCode?: string;
  multiplier?: number;
  callback?: () => void;
}

interface CountryProps {
  index: number;
  countryName: string;
  countryCode: string;
}

interface PlanProps {
  index: number;
  plan: string;
}

interface BasicCO2Props {
  countryCode: string;
  plan: string;
  employees: number;
  callback: (tonnes: number, priceData: number) => void;
}

export const initialState: State = {
  items: [],
  clonedItems: [],
  isLoading: true,
  currency: 'usd',
  process: 'create',
  error: '',
  currentStep: 'offsetPlan',
  cancelAtPeriodEnd: false,
  selectedPlanId: 'ClimatePositive',
};

const slice = createSlice({
  name: 'employeeBenefit',
  initialState,
  reducers: {
    setEmployeeBenefit: (state: State, action: PayloadAction<SubscriptionObject>): State => {
      state.items = action.payload.items;
      state.clonedItems = cloneDeep(state.items);
      state.subscriptionId = action.payload.id;
      state.currency = action.payload.currency ?? 'usd';
      state.latestOrderDate = action.payload.latestOrderDate ?? '';
      state.cancelAtPeriodEnd = action.payload.cancelAtPeriodEnd ?? false;
      state.newEmployeeInvites = [];
      state.newEmployeeOffsets = [];
      state.selectedPlanId = action.payload.items ? action.payload.items[0]?.details.person?.plan ?? plans[0].id : plans[0].id;
      return state;
    },
    clearEmployeeBenefit: (): State => {
      return initialState;
    },
    removeItem: (state: State, action: PayloadAction<number>): State => {
      state.items.splice(action.payload, 1);
      return state;
    },
    updateItemCO2: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value, price } = action.payload;
      const item = state.items[index];
      item.kilosCo2 = value;
      item.price = price || 0;
      return state;
    },
    updateItemPeopleState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.items[index];
      if (item.details.person) {
        item.details.person.personCount = value;
      }
      return state;
    },
    updateItemCountryState: (state: State, action: PayloadAction<CountryProps>): State => {
      const { index, countryCode, countryName } = action.payload;
      const item = state.items[index];
      if (item.details.person) {
        item.details.person.countryCode = countryCode;
        item.details.person.countryName = countryName;
      }
      return state;
    },
    updateItemPlanState: (state: State, action: PayloadAction<PlanProps>): State => {
      const { index, plan } = action.payload;
      const item = state.items[index];
      if (item.details.person) {
        item.details.person.plan = plan;
      }
      item.multiplier = getPlan(plan)?.multiplier;
      return state;
    },
    addItem: (state: State, action: PayloadAction<CreateProps>): State => {
      const item = {
        portfolioid: 'defaultco2',
        kilosCo2: action.payload.kilosCo2 || 1000,
        price: action.payload.price || 0,
        multiplier: action.payload.multiplier || plans[0].multiplier,
        details: {
          person: {
            category: 'Employee',
            countryCode: action.payload.countryCode || 'NO',
            countryName: action.payload.countryName || 'Norway',
            plan: action.payload.plan || state.selectedPlanId || plans[0].id,
            days: 0,
            months: 1,
            years: 0,
            personCount: action.payload.employees || 1,
          },
        },
      };
      state.items.push(item);
      return state;
    },
    refreshItems: (state: State): State => {
      state.items = cloneDeep(state.clonedItems);
      return state;
    },
    setProcess: (state: State, action: PayloadAction<ProcessType | undefined>): State => {
      if (action.payload) {
        state.process = action.payload;
        return state;
      }

      if (state.subscriptionId) {
        if (state.items.length) {
          state.process = 'update';
        } else {
          state.process = 'cancel';
        }
      } else {
        state.process = 'create';
      }

      return state;
    },
    setCurrency: (state: State, action: PayloadAction<string>): State => {
      state.currency = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setError: (state: State, action: PayloadAction<string>): State => {
      state.error = action.payload;
      return state;
    },
    setErrorType: (state: State, action: PayloadAction<string>): State => {
      state.errorType = action.payload;
      return state;
    },
    setCurrentStep: (state: State, action: PayloadAction<string>): State => {
      state.currentStep = action.payload;
      return state;
    },
    setNewEmployeeOffsets: (state: State, action: PayloadAction<NewEmployeeOffset[]>): State => {
      state.newEmployeeOffsets = action.payload;
      return state;
    },
    setNewEmployeeInvites: (state: State, action: PayloadAction<UserPayload[]>): State => {
      state.newEmployeeInvites = action.payload;
      return state;
    },
    setSelectedPlanId: (state: State, action: PayloadAction<string>): State => {
      state.selectedPlanId = action.payload;
      return state;
    },
  },
});

const {
  clearEmployeeBenefit,
  removeItem,
  addItem,
  setLoading,
  setProcess,
  setEmployeeBenefit,
  updateItemCO2,
  updateItemPeopleState,
  setError,
  setErrorType,
  updateItemCountryState,
  updateItemPlanState,
  setCurrency,
  refreshItems,
  setCurrentStep,
  setNewEmployeeInvites,
  setNewEmployeeOffsets,
  setSelectedPlanId,
} = slice.actions;
export {
  setEmployeeBenefit,
  clearEmployeeBenefit,
  removeItem,
  addItem,
  setProcess,
  setLoading,
  setError,
  updateItemCO2,
  refreshItems,
  setCurrentStep,
};
export default slice.reducer;

export const normalizeItems = (items: SubscriptionItem[]): SubscriptionItem[] =>
  items.map(item => {
    return {
      portfolioid: item.portfolioid || 'defaultco2',
      kilosCo2: item.kilosCo2 * (item.multiplier || plans[0].multiplier),
      price: item.price,
      details: {
        person: item.details.person,
      },
      feeFactors: {
        employeeBenefitMonthlyPersons: item.details.person?.personCount ?? 0,
      },
    };
  });

export const saveSubscription = (cb?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const { items, clonedItems, subscriptionId, process, newEmployeeInvites, newEmployeeOffsets } = getState().employeeBenefit;

  const employeeAmount = items.reduce<number>((prev, next) => (prev += next.details.person?.personCount ?? 0), 0);
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const normalizedItems = normalizeItems(items);
  const normalizedClonedItems = normalizeItems(clonedItems);
  const context = appContextSelector(getState());
  const employeeBenefitAPI = createEmployeeBenefitApi(context);

  try {
    if (process === 'update') {
      if (subscriptionId) {
        const payload = {
          interval: 'monthly',
          category: 'Employee benefit',
          description: `Employee benefit for ${employeeAmount} employees`,
          items: normalizedItems.length ? normalizedItems : normalizedClonedItems,
          ...(newEmployeeInvites !== undefined ? { newEmployeeInvites } : null),
          ...(newEmployeeOffsets !== undefined ? { newEmployeeOffsets } : null),
        };
        const data = await employeeBenefitAPI.update(subscriptionId, payload, currentUser?.customerId ?? '');

        if (data?.invites?.length) {
          dispatch(
            addFakeUsers(
              data.invites.map(invite => ({
                userAccount: { email: invite.userAccount.email },
                role: { id: invite.role.id, name: invite.role?.name },
                employeeCountryCodeIso2: invite.employeeCountryCodeIso2,
                employeeOffset: invite.employeeOffset,
                status: 'Invited',
                userId: invite.userId ?? '',
              })),
            ),
          );
        }

        dispatch(updateSubscription({ ...data.subscription }));
        dispatch(getUserSeats());
        dispatch(syncAttribution());
      }
    } else if (process === 'cancel') {
      if (subscriptionId) {
        const data = await employeeBenefitAPI.remove(subscriptionId, currentUser?.customerId ?? '');
        dispatch(updateSubscription({ ...data.subscription }));
        dispatch(syncAttribution());
      }
    } else {
      const subscription = {
        type: 'EmployeeBenefit',
        interval: 'monthly',
        category: 'Employee benefit',
        description: `Employee benefit for ${employeeAmount} employees`,
        items: normalizedItems,
        ...(newEmployeeInvites !== undefined ? { newEmployeeInvites } : null),
        ...(newEmployeeOffsets !== undefined ? { newEmployeeOffsets } : null),
      };
      const data = await employeeBenefitAPI.create(subscription, currentUser?.customerId ?? '');
      dispatch(createSubscription({ ...data.subscription }));
      dispatch(syncAttribution());
      setTag({
        ...tags.portalOffset.placeOrder,
        categories: [data.subscription?.category ?? ''],
        transactionId: data.orderId,
        transactionTotal: data.subscription.totalPrice,
        currencyCode: data.subscription.currency,
        transactionProducts: data.subscription.items.map(item => ({
          category: data.subscription.category ?? '',
          name: 'Employee benefit offset',
          price: roundNumberToTwo(item.price),
          quantity: item.kilosCo2,
          sku: 'employee-benefit-offset',
        })),
      });
    }

    cb && cb();
  } catch (err) {
    // eslint-disable-next-line
    console.error(err);
    dispatch(setError(process !== 'create' ? 'Unable to update a subscription plan.' : 'Unable to create a subscription plan.'));
  } finally {
    dispatch(setLoading(false));
  }
};

export const prepareEmployeeBenefit = (subscription: SubscriptionObject | undefined): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  dispatch(clearEmployeeBenefit());
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);

  try {
    if (subscription) {
      const normalizedSubscription = cloneDeep(subscription);
      for (const item of normalizedSubscription.items) {
        let multiplier = plans[0].multiplier;
        if (item.details.person) {
          multiplier = getPlan(item.details.person.plan)?.multiplier || plans[0].multiplier;
        }
        const data = await carbonApi.getCarbonOffsetPrice(
          {
            kilosCo2: item.kilosCo2,
            employeeBenefitMonthlyPersons: item.details.person?.personCount ?? 0,
          },
          currentUser?.customerId ?? '',
        );
        item.multiplier = multiplier;
        item.kilosCo2 = item.kilosCo2 / item.multiplier;
        item.price = data.salesPrice;
      }
      dispatch(setEmployeeBenefit(normalizedSubscription));

      if (normalizedSubscription.cancelAtPeriodEnd) {
        dispatch(setCurrentStep('cancelPlan'));
      }
    } else {
      const currency = currentUser?.customer?.payment?.currency ?? 'usd';
      dispatch(setCurrency(currency));
    }
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setError('Unable to fetch price for subscription items.'));
  }
};

export const activateEmployeeBenefit = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentUser = currentUserSelector(getState());
  const currency = currentUser?.customer?.payment?.currency ?? 'usd';
  dispatch(setCurrency(currency));
  dispatch(setCurrentStep('editPlan'));
};

export const employeeBenefitCountries = createSelector(
  (state: RootState) => state.employeeBenefit.items,
  items => {
    const countries = items.map(item => item.details.person?.countryName);
    return uniq(countries);
  },
);

export const employeeBenefitUnusedCountries = createSelector(supportedCountriesISO2Selector, employeeBenefitCountries, (countries, usedCountries) =>
  countries.filter(country => !usedCountries.some(usedCountry => usedCountry === country.label)),
);

export const employeeBenefitUnusedCountriesWithUserCountryOnTop = createSelector(
  employeeBenefitUnusedCountries,
  currentUserCountryCodeSelector,
  (countries, userCountryCode) => {
    const userCountryOption = countries.find(country => country.value === userCountryCode);
    const countriesWithoutUserCountry = countries.filter(country => country.value !== userCountryCode);

    return userCountryOption ? [userCountryOption, ...countriesWithoutUserCountry] : countries;
  },
);

export const addNewItem = (props?: CreateProps, cb?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const usedCountries = employeeBenefitCountries(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);

  const availableCountries = employeeBenefitUnusedCountriesWithUserCountryOnTop(getState());
  const { selectedPlanId } = getState().employeeBenefit;

  const availableCountry = availableCountries.find(country => !usedCountries.some(usedCountryName => usedCountryName === country?.label));
  const availableCountryCode = availableCountry?.value ?? '';
  const availableCountryName = availableCountry?.label ?? '';

  const countryCode = props?.countryCode ?? availableCountryCode;

  const employees = props?.employees ?? 1;
  const plan = getPlan(props?.plan ?? selectedPlanId);
  const currentUser = currentUserSelector(getState());

  try {
    if (plan) {
      const footprintData = await employeeBenefitAPI.getPercapitaFootprint(countryCode, employees);
      const kilosCo2 = footprintData.kilosCo2e;
      const multiplier = plan.multiplier || plans[0].multiplier;
      const priceData = await carbonApi.getCarbonOffsetPrice(
        {
          kilosCo2: footprintData.kilosCo2e * multiplier,
          employeeBenefitMonthlyPersons: employees,
        },
        currentUser?.customerId ?? '',
      );
      dispatch(
        addItem({
          ...props,
          multiplier,
          price: priceData.salesPrice,
          kilosCo2,
          countryCode: props?.countryCode ?? availableCountryCode,
          countryName: props?.countryName ?? availableCountryName,
        }),
      );
      cb && cb();
    } else {
      dispatch(setError('Unable to add new subscription item. Plan is incorrect.'));
    }
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setError('Unable to add new subscription item.'));
  }
};

export const updateItemPlan = (props: PlanProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const { index, plan } = props;
  const { items } = getState().employeeBenefit;
  const item = items[index];
  const multiplier = getPlan(plan)?.multiplier;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);

  try {
    const kilosCo2 = item.kilosCo2 * (multiplier || plans[0].multiplier);
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2,
        employeeBenefitMonthlyPersons: item.details.person?.personCount ?? 0,
      },
      currentUser?.customerId ?? '',
    );
    dispatch(updateItemCO2({ index, value: item.kilosCo2, price: priceData.salesPrice }));
    dispatch(updateItemPlanState({ ...props }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setError('Unable to update plan for subscription item.'));
  }
};

export const updateItemCountry = (props: CountryProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const { items } = getState().employeeBenefit;
  const item = items[props.index];
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);

  try {
    const data = await employeeBenefitAPI.getPercapitaFootprint(props.countryCode, item.details.person?.personCount ?? 0);
    const kilosCo2 = data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2: data.kilosCo2e * (item.multiplier || plans[0].multiplier),
        employeeBenefitMonthlyPersons: item.details.person?.personCount ?? 0,
      },
      currentUser?.customerId ?? '',
    );
    dispatch(updateItemCO2({ index: props.index, value: kilosCo2, price: priceData.salesPrice }));
    dispatch(updateItemCountryState({ ...props }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setErrorType('country'));
    dispatch(setError('Unable to update country for subscription item.'));
  }
};

export const updateItemPeople = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const { items } = getState().employeeBenefit;
  const item = items[props.index];
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);

  try {
    const data = await employeeBenefitAPI.getPercapitaFootprint(item.details.person?.countryCode ?? 'NO', props.value);
    const kilosCo2 = data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2: data.kilosCo2e * (item.multiplier || plans[0].multiplier),
        employeeBenefitMonthlyPersons: props.value ?? 0,
      },
      currentUser?.customerId ?? '',
    );
    dispatch(updateItemCO2({ index: props.index, value: kilosCo2, price: priceData.salesPrice }));
    dispatch(updateItemPeopleState({ ...props }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setError('Unable to update number of people for subscription item.'));
  }
};

export const getCO2ForTemporaryItem = (props: BasicCO2Props): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const { countryCode, employees, plan, callback } = props;
  const multiplier = getPlan(plan)?.multiplier || plans[0].multiplier;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const employeeBenefitAPI = createEmployeeBenefitApi(context);

  try {
    const data = await employeeBenefitAPI.getPercapitaFootprint(countryCode, employees);
    const kilosCo2 = multiplier * data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2,
        employeeBenefitMonthlyPersons: employees,
      },
      currentUser?.customerId ?? '',
    );
    callback(kilosCo2, priceData.salesPrice);
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setErrorType('country'));
    dispatch(setError('Unable to fetch amount of CO2 for subscription item.'));
  }
};

export const addExistingUserOffset = (userId: string, employeeCountryCodeIso2: string): AppThunk => (dispatch, getState): void => {
  const { newEmployeeOffsets } = getState().employeeBenefit;
  dispatch(setNewEmployeeOffsets([...(newEmployeeOffsets ?? []), { userId, employeeCountryCodeIso2 }]));
};

export const updateSelectedPlan = (plan: string): AppThunk => (dispatch, getState): void => {
  const { items } = getState().employeeBenefit;
  dispatch(setSelectedPlanId(plan));
  items.forEach((_, index) => dispatch(updateItemPlan({ index, plan })));
};

export const addNewEmployeeInvites = (payload: UserPayload[]): AppThunk => (dispatch, getState): void => {
  const { newEmployeeInvites } = getState().employeeBenefit;
  dispatch(setNewEmployeeInvites([...(newEmployeeInvites ?? []), ...payload]));
};

export const employeeBenefitTotalKilosCO2 = createSelector(
  (state: RootState) => state.employeeBenefit.items,
  items => items.reduce<number>((prev, next) => prev + next.kilosCo2 * (next.multiplier || plans[0].multiplier), 0),
);

export const employeeBenefitTotalPrice = createSelector(
  (state: RootState) => state.employeeBenefit.items,
  items => items.reduce<number>((prev, next) => (prev += next.price), 0),
);

export const employeeBenefitTotalKilosCO2FromCopy = createSelector(
  (state: RootState) => state.employeeBenefit.clonedItems,

  items => items.reduce<number>((prev, next) => (prev += next.kilosCo2 * (next.multiplier || plans[0].multiplier)), 0),
);

export const employeeBenefitTotalPriceFromCopy = createSelector(
  (state: RootState) => state.employeeBenefit.clonedItems,
  items => items.reduce<number>((prev, next) => (prev += next.price), 0),
);

export const employeeBenefitAmountOfPeople = createSelector(
  (state: RootState) => state.employeeBenefit.items,
  items => items.reduce<number>((prev, next) => (prev += next.details.person?.personCount ?? 0), 0),
);

export const employeeBenefitSelectedPlan = createSelector(
  (state: RootState) => state.employeeBenefit.items,
  items => (items ? items[0]?.details.person?.plan ?? '' : ''),
);
